<template lang="pug">
#recruitment-contact-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 入所募集お問い合わせの対応登録・修正

    .box-body.register-body
      //- contactDatetime
      FormLabel(v-if="recruitmentContact" key-name="お問い合わせ日時" :value="_contactDatetime")
      //- approved
      .d-flex.flex-column.mb-3(style="margin-left:20px;")
        span.approved-name 受付状態
        SupportDoneSwitch(v-model="done")
      //- name
      FormLabel(v-if="recruitmentContact" key-name="氏名" :value="recruitmentContact.name")
      //- tel
      FormLabel(v-if="recruitmentContact" key-name="電話番号" :value="recruitmentContact.tel")
      //- contactMessage
      FormLabel(v-if="recruitmentContact" key-name="お問い合わせ内容" :value="recruitmentContact.contactMessage")
      //- customerName
      FormInput(v-model="customerName" key-name="担当者" name="customerName" type="text" placeholder="担当者" :maxlength="100"
        :validation-rules="{ max: 100 }")
      //- supportMemo
      FormTextarea(v-model="supportMemo" key-name="対応メモ" name="support-memo" placeholder="対応メモ" :maxlength="1000" :rows="7"
        :validation-rules="{ max: 1000 }" :styles="{ maxWidth: '640px', width: '640px' }")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="onUpdate" type="button") 更新
        button.btn.cancel-btn(@click="onCancel" type="button") キャンセル

  OkCancelModal(v-model="showUpdateConfirmModal" title="入所募集問い合わせ情報を更新しますか？" @ok="updateRecruitmentContact")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoRecruitmentContactList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import RecruitmentContactsService, { RecruitmentContactsGetModel, RecruitmentContactsPatchModel } from "@/commons/services/RecruitmentContacts";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import SupportDoneSwitch from "@/pc/basics/SupportDoneSwitch.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";

/**
 * ## 入所募集問い合わせ編集画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    SupportDoneSwitch,
    FormDatetimePicker,
    FormInput,
    FormTextarea,
    FormLabel
  },
  $_veeValidate: { validator: "new" }
})
export default class RecruitmentContactEdit extends Vue {
  /** 入所募集問い合わせサービス */
  @Inject("RecruitmentContactsService") recruitmentContactsService!: RecruitmentContactsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateConfirmModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 入所募集問い合わせ */
  recruitmentContact: RecruitmentContactsGetModel | null = null;
  /** 対応状態 */
  done: boolean = false;
  /** 担当者 */
  customerName: string | null = "";
  /** 入所募集問い合わせ、対応メモ */
  supportMemo: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  get _contactDatetime(): string {
    if (!this.recruitmentContact) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(this.recruitmentContact.contactDatetime, "yyyy年mm月dd日 hh：MM");
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 既存値を初期入力するために入所募集問い合わせをAPIから取得する
   *   - 前画面から渡された入所募集問い合わせのソートキーを取得して使用する
   * - 見学日時の値は既存入力されていれば復元し、無ければカレンダー上の初期値を作成する
   */
  public async createView(): Promise<void> {
    const recruitmentContactsSortKey: string = this.$route.params.datetime;
    const recruitmentContact: RecruitmentContactsGetModel = await this.getRecruitmentContact(recruitmentContactsSortKey);
    this.recruitmentContact = recruitmentContact;

    this.done = recruitmentContact.done;
    this.customerName = recruitmentContact.customerName ? recruitmentContact.customerName : "";
    this.supportMemo = recruitmentContact.supportMemo ? recruitmentContact.supportMemo : "";

    return;
  }

  /**
   * ### 編集対象の入所募集問い合わせ取得
   */
  public async getRecruitmentContact(recruitmentContactsSortKey: string): Promise<RecruitmentContactsGetModel> {
    const response: RecruitmentContactsGetModel = await this.recruitmentContactsService.get(userStore, recruitmentContactsSortKey);
    return response;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async onUpdate(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateConfirmModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 入所募集問い合わせを更新する
   */
  public async updateRecruitmentContact(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const recruitmentContact: RecruitmentContactsGetModel = this.recruitmentContact as RecruitmentContactsGetModel;
      const requestBody: RecruitmentContactsPatchModel = this.createRequestBody(recruitmentContact);
      await this.recruitmentContactsService.patch(userStore, recruitmentContact.recruitmentContactsSortKey, requestBody);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    // サイドメニューを更新
    this.$emit("refresh");

    return;
  }

  /**
   * ### 入所募集問い合わせ更新のリクエストボディを作成する
   */
  public createRequestBody(recruitmentContact: RecruitmentContactsGetModel): RecruitmentContactsPatchModel {
    return {
      done: this.done,
      customerName: this.customerName,
      supportMemo: this.supportMemo,
      updated: recruitmentContact.updated
    };
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public onCancel(): void {
    this.gotoRecruitmentContactList();
    return;
  }

  /**
   * ### 入所募集問い合わせ一覧に遷移する
   */
  public gotoRecruitmentContactList(): void {
    this.$router.push(PcUrl.RecruitmentContactList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#recruitment-contact-edit {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .approved-name {
    height: 27px;
    margin-bottom: 4px;
    font-size: 18px;
    font-weight: bold;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
